import React, { useState, useRef, useEffect, useCallback } from "react"
import { getSuggestions, parseTemplateSegments } from "./templateSuggestions"

/**
 * Template-aware textarea with syntax highlighting and autocomplete (Liquid syntax)
 *
 * Features:
 * - Highlights {{ ... }} template variables in light blue
 * - Shows autocomplete dropdown when typing {{ with smart suggestions
 * - Supports namespaces: params, details, variables, auth
 * - Supports Liquid filters: date_value, format_date, add_duration, url_encode, etc.
 * - Supports multi-line content (request bodies, JSON, etc.)
 * - Tab key inserts spaces instead of changing focus
 */
export function TemplateTextarea({
  value,
  onChange,
  placeholder,
  rows = 8,
  className = "",
  disabled = false,
  id
}) {
  const [ showSuggestions, setShowSuggestions ] = useState(false)
  const [ suggestions, setSuggestions ] = useState([])
  const [ replaceRange, setReplaceRange ] = useState({ start: 0, end: 0 })
  const [ selectedIndex, setSelectedIndex ] = useState(0)
  const [ dropdownPosition, setDropdownPosition ] = useState({ top: 0, left: 0 })
  const textareaRef = useRef(null)
  const backdropRef = useRef(null)
  const containerRef = useRef(null)

  // Shared text styles that MUST match between textarea and backdrop
  const sharedTextStyles = {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "12px",
    lineHeight: "1.5",
    padding: "8px 12px",
    border: "1px solid transparent",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
    overflowWrap: "break-word"
  }

  // Update suggestions when cursor moves or text changes
  const updateSuggestions = useCallback(() => {
    if (!textareaRef.current) return

    const cursorPos = textareaRef.current.selectionStart || 0
    const { suggestions: newSuggestions, replaceStart, replaceEnd } = getSuggestions(value || "", cursorPos)

    setSuggestions(newSuggestions)
    setReplaceRange({ start: replaceStart, end: replaceEnd })
    setShowSuggestions(newSuggestions.length > 0)
    setSelectedIndex(0)

    // Calculate dropdown position based on cursor
    if (newSuggestions.length > 0 && textareaRef.current) {
      const pos = getCaretCoordinates(textareaRef.current, cursorPos)
      setDropdownPosition({
        top: pos.top + 24, // Below the cursor line
        left: Math.min(pos.left, 300) // Don't go too far right
      })
    }
  }, [ value ])

  // Handle input changes
  function handleChange(e) {
    onChange(e.target.value)
  }

  // Handle keyboard events
  function handleKeyDown(e) {
    // Tab inserts spaces when suggestions not shown
    if (e.key === "Tab" && !showSuggestions) {
      e.preventDefault()
      const target = e.target
      const start = target.selectionStart
      const end = target.selectionEnd
      const currentValue = value || ""
      const newValue = currentValue.substring(0, start) + "  " + currentValue.substring(end)
      onChange(newValue)
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 2
      }, 0)
      return
    }

    if (!showSuggestions || suggestions.length === 0) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex((prev) => (prev + 1) % suggestions.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length)
    } else if (e.key === "Enter" && showSuggestions) {
      e.preventDefault()
      selectSuggestion(suggestions[selectedIndex])
    } else if (e.key === "Tab" && showSuggestions) {
      e.preventDefault()
      selectSuggestion(suggestions[selectedIndex])
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
    }
  }

  // Select a suggestion and insert it
  function selectSuggestion(suggestion) {
    const before = (value || "").slice(0, replaceRange.start)
    const after = (value || "").slice(replaceRange.end)

    // Determine what to insert based on suggestion type
    let insertValue
    let cursorOffset = 0

    if (suggestion.isNamespace) {
      // Namespace: insert prefix, cursor stays at end
      insertValue = suggestion.value
    } else if (suggestion.isFilter) {
      // Filter: insert filter, cursor after it (allow chaining more filters)
      insertValue = suggestion.value
    } else {
      // Variable: insert value with closing }}, cursor before }}
      insertValue = suggestion.value + " }}"
      cursorOffset = 3
    }

    const newValue = before + insertValue + after
    onChange(newValue)
    setShowSuggestions(false)

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + insertValue.length - cursorOffset
        textareaRef.current.setSelectionRange(newPos, newPos)
        textareaRef.current.focus()
      }
    }, 0)
  }

  // Sync scroll between textarea and backdrop
  function handleScroll() {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft
    }
  }

  // Update suggestions on cursor movement
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    function handleSelectionChange() {
      if (document.activeElement === textarea) {
        updateSuggestions()
      }
    }

    document.addEventListener("selectionchange", handleSelectionChange)

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange)
    }
  }, [ updateSuggestions ])

  // Update suggestions when value changes
  useEffect(() => {
    updateSuggestions()
  }, [ value, updateSuggestions ])

  // Render highlighted segments
  const segments = parseTemplateSegments(value || "")

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Backdrop layer - shows highlights behind the textarea */}
      <div
        ref={backdropRef}
        className="absolute inset-0 pointer-events-none overflow-auto rounded-md"
        aria-hidden="true"
        style={sharedTextStyles}
      >
        {segments.map((seg, i) =>
          seg.isTemplate ? (
            <span
              key={i}
              className="bg-sky-200 dark:bg-sky-800 rounded-sm text-transparent"
            >
              {seg.text}
            </span>
          ) : (
            <span key={i} className="text-transparent">{seg.text}</span>
          )
        )}
      </div>

      {/* Actual textarea - sits on top with transparent background */}
      <textarea
        ref={textareaRef}
        id={id}
        value={value || ""}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onScroll={handleScroll}
        onClick={updateSuggestions}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className="relative block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent dark:text-gray-100"
        style={sharedTextStyles}
        autoComplete="off"
      />

      {/* Autocomplete dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          className="absolute z-50 max-h-48 overflow-auto rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: "300px",
            maxWidth: "400px"
          }}
        >
          <ul className="py-1">
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.value}
                onClick={() => selectSuggestion(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`
                  cursor-pointer px-3 py-2 text-sm
                  ${index === selectedIndex
                    ? "bg-blue-50 dark:bg-blue-900/30"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <code className={`font-mono text-xs ${suggestion.color || "text-blue-600 dark:text-blue-400"}`}>
                    {suggestion.isFilter
                      ? `|${suggestion.value}`
                      : suggestion.isNamespace
                        ? `{{ ${suggestion.value}`
                        : `{{ ${suggestion.value} }}`}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 truncate">
                    {suggestion.description}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/**
 * Get pixel coordinates of caret position in textarea
 */
function getCaretCoordinates(element, position) {
  const div = document.createElement("div")
  const style = getComputedStyle(element)

  const properties = [
    "fontFamily", "fontSize", "fontWeight", "fontStyle",
    "letterSpacing", "textTransform", "wordSpacing",
    "textIndent", "whiteSpace", "wordWrap", "wordBreak",
    "paddingLeft", "paddingRight", "paddingTop", "paddingBottom",
    "borderLeftWidth", "borderRightWidth", "borderTopWidth", "borderBottomWidth",
    "boxSizing", "lineHeight"
  ]

  properties.forEach((prop) => {
    div.style[prop] = style[prop]
  })

  div.style.position = "absolute"
  div.style.visibility = "hidden"
  div.style.whiteSpace = "pre-wrap"
  div.style.width = element.offsetWidth + "px"
  div.style.overflow = "hidden"

  const textBeforeCursor = element.value.substring(0, position)
  const textNode = document.createTextNode(textBeforeCursor)
  const span = document.createElement("span")
  span.textContent = "|"

  div.appendChild(textNode)
  div.appendChild(span)
  document.body.appendChild(div)

  const coordinates = {
    top: span.offsetTop - element.scrollTop,
    left: span.offsetLeft - element.scrollLeft
  }

  document.body.removeChild(div)

  return coordinates
}

export default TemplateTextarea
